import { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService, CustomFieldDefinition, CustomFieldValue } from '../services/api';
import { SortField, SortState } from '../types/sorting';
import { User } from '../types';

interface DisplayFounder {
  id: string;
  name: string;
  email: string;
  company: string;
  companyInitial: string;
  companyColor: string;
  stage: string;
  teamMember: string;
  teamMemberInitials?: string;
  teamMemberColor?: string;
  lastPostSummary?: string;
  lastPostSummaryDate?: string; // When the LinkedIn summary was last updated
  lastPostDate?: string; // When the actual LinkedIn post was published
  lastPostExcerpt?: string; // Excerpt from the latest post
  nextAction?: string; // Next action for this founder
  profileUrl?: string;
  roleTitle?: string;
  customFieldValues?: CustomFieldValue[];
  industry?: string;
  location?: string;
  followerCount?: number;
  priorityScore?: number;
  status?: string;
  tag?: string;
  owner?: string;
  lastTouchAt?: string;
  notes?: Array<{ id: string; content: string; createdAt: string }>;
  tabId?: string; // Legacy field
  tabIds?: string[]; // Array of tab names this founder belongs to
  selected?: boolean;
}

interface FilterState {
  name: string;
  email: string;
  company: string;
  linkedinUrl?: string;
  stage: string;
  industry?: string;
  location?: string;
  status?: string;
  tag?: string;
  teamMember: string;
}

// SortField, SortDirection, and SortState are now imported from '../types/sorting'

const companyColors = [
  '#ef4444', '#3b82f6', '#8b5cf6', '#7c3aed', '#6b7280', '#10b981', 
  '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16', '#f97316'
];

const getCompanyColor = (company: string, index: number) => 
  company ? companyColors[index % companyColors.length] : '';

const getInitials = (name: string) => 
  name.split(' ').map(n => n[0]).join('').toUpperCase();

const formatUpdateTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
  const diffInDays = Math.floor(diffInHours / 24);
  
  // If less than 24 hours, show relative time
  if (diffInHours < 24) {
    if (diffInHours < 1) {
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      return diffInMinutes < 1 ? 'Just now' : `${diffInMinutes}m ago`;
    }
    return `${diffInHours}h ago`;
  }
  
  // If less than 7 days, show days ago
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }
  
  // Otherwise show full date with time
  return `${date.getDate()} ${date.toLocaleString('en-US', { month: 'long' })} ${date.getFullYear()} at ${date.toLocaleString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  })}`;
};

export const useDataTable = (userId: string, user?: User | null, customFieldsRefreshTrigger?: number) => {
  const [founders, setFounders] = useState<DisplayFounder[]>([]);
  const [filteredFounders, setFilteredFounders] = useState<DisplayFounder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const loadVisible = (uid: string, userObj?: User | null): string[] => {
    const MANDATORY_COLUMNS = ['no', 'name'];

    // PRIORITY 1: If user has Notion column order, use that (source of truth from database)
    if (userObj?.notionColumnOrder && Array.isArray(userObj.notionColumnOrder) && userObj.notionColumnOrder.length > 0) {
      // Use Notion column order: ['no', 'name', ...customFieldIds, 'update']
      const notionFieldMapping = userObj.notionFieldMapping as any;
      const customFieldIds: string[] = [];

      // Map Notion column names to custom field IDs
      userObj.notionColumnOrder.forEach((colName: string) => {
        if (colName.toLowerCase() === 'name') return; // Skip Name since it's already included

        const mapping = notionFieldMapping?.[colName];
        if (mapping && mapping.customFieldId) {
          customFieldIds.push(mapping.customFieldId);
        }
      });

      return ['no', 'name', ...customFieldIds, 'update'];
    }

    // PRIORITY 2: Check localStorage (for users without Notion)
    try {
      const raw = localStorage.getItem(`people_visible_columns_${uid}`);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            // Ensure mandatory columns are always included
            const combined = [...MANDATORY_COLUMNS, ...parsed];
            // Remove 'tag' if present (user doesn't want it)
            const filtered = combined.filter(col => col !== 'tag');
            const unique = Array.from(new Set(filtered));
            return unique as string[];
          }
        }
    } catch {}

    // PRIORITY 3: Default fallback
    return ['no', 'name', 'update'];
  };
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => loadVisible(userId, user));
  const [filters, setFilters] = useState<FilterState>({
    name: '', email: '', company: '', linkedinUrl: '', stage: '', industry: '', location: '', status: '', tag: '', teamMember: ''
  });
  const [sortState, setSortState] = useState<SortState>({ field: null, direction: null });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Listen for column updates from Notion mapping
  useEffect(() => {
    const handleColumnsUpdated = () => {
      console.log('🔔 columns-updated event fired');
      console.log('  User notionColumnOrder:', user?.notionColumnOrder);
      console.log('  User notionFieldMapping:', user?.notionFieldMapping ? Object.keys(user.notionFieldMapping) : 'none');
      const newColumns = loadVisible(userId, user);
      console.log('  New columns:', newColumns);
      setVisibleColumns(newColumns);
    };

    window.addEventListener('columns-updated', handleColumnsUpdated);
    return () => {
      window.removeEventListener('columns-updated', handleColumnsUpdated);
    };
  }, [userId, user]);

  // Update visible columns when user's notionColumnOrder changes
  useEffect(() => {
    console.log('👤 User notionColumnOrder changed:', user?.notionColumnOrder);
    if (user?.notionColumnOrder && user.notionColumnOrder.length > 0) {
      const newColumns = loadVisible(userId, user);
      console.log('  Loading new columns:', newColumns);
      setVisibleColumns(newColumns);
    }
  }, [user?.notionColumnOrder, user?.notionFieldMapping, userId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [foundersResponse, customFieldsResponse] = await Promise.all([
          apiService.getFounders(100),
          apiService.getCustomFields()
        ]);
        
        setCustomFields(customFieldsResponse);
        
        // Clean up invalid column IDs from visibleColumns
        const builtinColumns = ['no', 'name', 'email', 'company', 'position', 'notes', 'update',
                               'stage', 'industry', 'location', 'status', 'owner', 'linkedinUrl',
                               'xUrl', 'substackUrl', 'accelerator', 'contactPhone', 'lastEditedBy',
                               'type', 'vcFirm', 'followerCount',
                               'priorityScore', 'lastTouchAt'];
        const validCustomFieldIds = customFieldsResponse.map(f => f.id);
        
        // Get current visible columns (from state, which was initialized from localStorage)
        setVisibleColumns(current => {
          const validVisible = current.filter(col => {
            // Filter out 'tag' (user doesn't want it)
            if (col === 'tag') return false;
            // Keep built-in columns
            if (builtinColumns.includes(col)) return true;
            // Keep only custom fields that actually exist
            return validCustomFieldIds.includes(col);
          });
          
          // Update localStorage if we removed any invalid ones
          if (validVisible.length !== current.length) {
            console.log('🧹 Cleaned up invalid columns:', {
              before: current.length,
              after: validVisible.length,
              removed: current.filter(col => !validVisible.includes(col))
            });
            localStorage.setItem(`people_visible_columns_${userId}`, JSON.stringify(validVisible));
            return validVisible;
          }
          
          return current;
        });
        
        const displayFounders: DisplayFounder[] = foundersResponse.data.map((founder, index) => ({
          id: founder.id,
          name: founder.name,
          email: founder.email || '',
          company: founder.company || '',
          companyInitial: founder.company ? founder.company[0].toUpperCase() : '',
          companyColor: getCompanyColor(founder.company || '', index),
          stage: founder.stage || '',
          teamMember: founder.owner || 'System',
          teamMemberInitials: founder.owner ? getInitials(founder.owner) : undefined,
          teamMemberColor: founder.owner ? '#10b981' : undefined,
          lastPostSummary: founder.lastPostSummary || undefined,
          lastPostSummaryDate: founder.lastPostSummaryDate || undefined,
          lastPostDate: founder.lastPostDate || undefined,
          lastPostExcerpt: founder.lastPostExcerpt || undefined,
          nextAction: founder.nextAction || undefined,
          customFieldValues: founder.customFieldValues || [],
          industry: founder.industry,
          location: founder.location,
          followerCount: founder.followerCount,
          priorityScore: founder.priorityScore,
          status: founder.status,
          tag: founder.tag,
          owner: founder.owner,
          lastTouchAt: founder.lastTouchAt,
          profileUrl: founder.profileUrl,
          roleTitle: (founder as any).roleTitle,
          tabId: (founder as any).tabId, // Legacy field
          tabIds: (founder as any).tabIds || ['All People'] // Default to ["All People"] if not present
        }));
        
        setFounders(displayFounders);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [customFieldsRefreshTrigger]);

  // Listen for custom fields update events (e.g., when new multiselect options are added)
  useEffect(() => {
    const handleCustomFieldsUpdate = async () => {
      try {
        const customFieldsResponse = await apiService.getCustomFields();
        setCustomFields(customFieldsResponse);
      } catch (error) {
        console.error('Error refreshing custom fields:', error);
      }
    };

    window.addEventListener('customFieldsUpdated', handleCustomFieldsUpdate);
    return () => {
      window.removeEventListener('customFieldsUpdated', handleCustomFieldsUpdate);
    };
  }, []);

  // Memoize custom field IDs to prevent unnecessary re-renders and keep dependency array stable
  const tagFieldId = useMemo(() => {
    const field = customFields.find(f => {
      const name = (f as any).name?.toLowerCase() || '';
      const label = f.label?.toLowerCase() || '';
      return name === 'tag' || name === 'tags' || label === 'tag' || label === 'tags';
    });
    return field?.id || null;
  }, [customFields]);

  const statusFieldId = useMemo(() => {
    const field = customFields.find(f => {
      const name = (f as any).name?.toLowerCase() || '';
      const label = f.label?.toLowerCase() || '';
      return name === 'status' || label === 'status';
    });
    return field?.id || null;
  }, [customFields]);

  // Create a stable reference for customFields dependency - use a string of all IDs
  const customFieldsKey = useMemo(() => customFields.map(f => f.id).sort().join(','), [customFields]);

  useEffect(() => {

    const filtered = founders.filter(founder => {
      return Object.entries(filters).every(([key, value]) => {
        if (!value) return true;
        const v = value.toLowerCase();
        
        if (key === 'linkedinUrl') {
          return (founder.profileUrl || '').toLowerCase().includes(v);
        }
        
        if (key === 'tag' && tagFieldId) {
          // Find tag value in custom field values
          const tagValue = founder.customFieldValues?.find(cfv => cfv.fieldId === tagFieldId)?.value || '';
          return tagValue.toLowerCase().includes(v);
        }
        
        if (key === 'status' && statusFieldId) {
          // Find status value in custom field values
          const statusValue = founder.customFieldValues?.find(cfv => cfv.fieldId === statusFieldId)?.value || '';
          // Also check built-in status field if custom field is empty
          const builtInStatus = founder.status || '';
          const combinedStatus = statusValue || builtInStatus;
          return combinedStatus.toLowerCase().includes(v);
        }
        
        // Handle optional fields
        const fieldValue = founder[key as keyof DisplayFounder];
        if (fieldValue === undefined || fieldValue === null) return false;
        return String(fieldValue).toLowerCase().includes(v);
      });
    });

    const sorted = !sortState.field || !sortState.direction 
      ? [...filtered].reverse() // When no sort, reverse to show newest at bottom
      : [...filtered].sort((a, b) => {
          let aValue: string | number = '';
          let bValue: string | number = '';

          switch (sortState.field) {
            case 'name': aValue = a.name.toLowerCase(); bValue = b.name.toLowerCase(); break;
            case 'email': aValue = a.email.toLowerCase(); bValue = b.email.toLowerCase(); break;
            case 'company': aValue = a.company.toLowerCase(); bValue = b.company.toLowerCase(); break;
            case 'position': aValue = (a.roleTitle || '').toLowerCase(); bValue = (b.roleTitle || '').toLowerCase(); break;
            case 'stage': aValue = a.stage.toLowerCase(); bValue = b.stage.toLowerCase(); break;
            case 'teamMember': aValue = a.teamMember.toLowerCase(); bValue = b.teamMember.toLowerCase(); break;
            case 'notes': aValue = a.notes?.length || 0; bValue = b.notes?.length || 0; break;
          }

          const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
          return sortState.direction === 'asc' ? comparison : -comparison;
        });

    setFilteredFounders(sorted);
  }, [founders, filters, sortState, tagFieldId, statusFieldId, customFieldsKey]);

  const handleSort = (field: SortField) => {
    setSortState(prev => {
      if (prev.field === field) {
        if (prev.direction === 'asc') return { field, direction: 'desc' };
        if (prev.direction === 'desc') return { field: null, direction: null };
        return { field, direction: 'asc' };
      }
      return { field, direction: 'asc' };
    });
  };

  const handleFilterChange = (field: keyof FilterState, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleToggleColumn = (columnId: string) => {
    // Prevent toggling mandatory columns
    const MANDATORY_COLUMNS = ['name'];
    if (MANDATORY_COLUMNS.includes(columnId)) {
      return; // Don't allow toggling mandatory columns
    }
    
    setVisibleColumns(prev => {
      const next = prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId];
      // Ensure mandatory columns are always included
      const combined = [...MANDATORY_COLUMNS, ...next];
      const withMandatory = Array.from(new Set(combined));
      try { localStorage.setItem(`people_visible_columns_${userId}`, JSON.stringify(withMandatory)); } catch {}
      return withMandatory;
    });
  };

  // Persist on initial load as well (in case defaults were used)
  // Ensure mandatory columns are always included when saving
  useEffect(() => {
    const MANDATORY_COLUMNS = ['name'];
    const combined = [...MANDATORY_COLUMNS, ...visibleColumns];
    const withMandatory = Array.from(new Set(combined));
    try { localStorage.setItem(`people_visible_columns_${userId}`, JSON.stringify(withMandatory)); } catch {}
  }, [userId, visibleColumns]);

  const refreshFounders = useCallback(async (onNewFounderDetected?: (founderId: string, tabId?: string) => void) => {
    try {
      setLoading(true);

      // Store current founder IDs to detect new ones
      const currentFounderIds = new Set(founders.map(f => f.id));

      const foundersResponse = await apiService.getFounders(100);

      const displayFounders: DisplayFounder[] = foundersResponse.data.map((founder, index) => ({
        id: founder.id,
        name: founder.name,
        email: founder.email || '',
        company: founder.company || '',
        companyInitial: founder.company ? founder.company[0].toUpperCase() : '',
        companyColor: getCompanyColor(founder.company || '', index),
        stage: founder.stage || '',
        teamMember: founder.owner || 'System',
        teamMemberInitials: founder.owner ? getInitials(founder.owner) : undefined,
        teamMemberColor: founder.owner ? '#10b981' : undefined,
        lastPostSummary: founder.lastPostSummary || undefined,
        customFieldValues: founder.customFieldValues || [],
        industry: founder.industry,
        location: founder.location,
        followerCount: founder.followerCount,
        priorityScore: founder.priorityScore,
        status: founder.status,
        tag: founder.tag,
        owner: founder.owner,
        lastTouchAt: founder.lastTouchAt,
        profileUrl: founder.profileUrl,
        roleTitle: (founder as any).roleTitle,
        tabId: (founder as any).tabId
      }));

      // Detect newly added founders
      if (currentFounderIds.size > 0 && onNewFounderDetected) {
        const newFounders = displayFounders.filter(founder => !currentFounderIds.has(founder.id));
        newFounders.forEach(founder => {
          console.log('🆕 New founder detected:', founder.name, founder.id, 'tabId:', founder.tabId);
          onNewFounderDetected(founder.id, founder.tabId);
        });
      }

      setFounders(displayFounders);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh data');
    } finally {
      setLoading(false);
    }
  }, [founders]);

  const handleToggleSelect = (founderId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(founderId);
      } else {
        newSet.delete(founderId);
      }
      return newSet;
    });
    
    setFounders(prev => prev.map(f => 
      f.id === founderId ? { ...f, selected: checked } : f
    ));
  };

  const handleToggleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(new Set(filteredFounders.map(f => f.id)));
      setFounders(prev => prev.map(f => ({ ...f, selected: true })));
    } else {
      setSelectedIds(new Set());
      setFounders(prev => prev.map(f => ({ ...f, selected: false })));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    
    try {
      const deletePromises = Array.from(selectedIds).map(id => 
        apiService.deleteFounder(id)
      );
      await Promise.all(deletePromises);
      
      // Refresh the founders list
      await refreshFounders();
      
      // Clear selection
      setSelectedIds(new Set());
    } catch (error) {
      console.error('Error deleting founders:', error);
      throw error;
    }
  };

  const handleFounderUpdate = (founderId: string, updates: Partial<DisplayFounder>) => {
    // Optimistically update the local state
    setFounders(prev => prev.map(f => 
      f.id === founderId ? { ...f, ...updates } : f
    ));
  };

  const addFounderToList = useCallback((founder: any) => {
    // Convert API founder to DisplayFounder format and add to list
    const displayFounder: DisplayFounder = {
      id: founder.id,
      name: founder.name,
      email: founder.email || '',
      company: founder.company || '',
      companyInitial: founder.company ? founder.company[0].toUpperCase() : '',
      companyColor: getCompanyColor(founder.company || '', founders.length),
      stage: founder.stage || '',
      teamMember: founder.owner || 'System',
      teamMemberInitials: founder.owner ? getInitials(founder.owner) : undefined,
      teamMemberColor: founder.owner ? '#10b981' : undefined,
      lastPostSummary: founder.lastPostSummary || undefined,
      lastPostSummaryDate: founder.lastPostSummaryDate || undefined,
      lastPostDate: founder.lastPostDate || undefined,
      customFieldValues: founder.customFieldValues || [],
      industry: founder.industry,
      location: founder.location,
      followerCount: founder.followerCount,
      priorityScore: founder.priorityScore,
      status: founder.status,
      tag: (founder as any).tag,
      owner: founder.owner,
      lastTouchAt: founder.lastTouchAt,
      profileUrl: founder.profileUrl,
      roleTitle: (founder as any).roleTitle,
      tabId: (founder as any).tabId, // Legacy field
      tabIds: (founder as any).tabIds || ['All People'] // Default to ["All People"] if not present
    };
    
    // Add to the beginning of the list so it appears at the bottom after reversing
    // (Backend returns newest first, we reverse to show oldest first, so prepend makes it oldest)
    setFounders(prev => [displayFounder, ...prev]);
  }, [founders.length, getCompanyColor, getInitials]);

  return {
    founders,
    filteredFounders,
    loading,
    error,
    customFields,
    visibleColumns,
    filters,
    sortState,
    selectedIds,
    handleSort,
    handleFilterChange,
    handleToggleColumn,
    handleToggleSelect,
    handleToggleSelectAll,
    handleBulkDelete,
    handleFounderUpdate,
    addFounderToList,
    refreshFounders,
    getInitials,
    formatUpdateTime,
    setVisibleColumns: setVisibleColumns,
    setFilters: setFilters,
    setSortState: setSortState,
    setSelectedIds: setSelectedIds,
    setFounders: setFounders
  };
};
